import { c as _c } from "react/compiler-runtime";
import { IconCheck, IconPencil, IconShoppingCart } from "@tabler/icons-react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { useTrackSegmentInViewport } from "@/hooks/useTrackSegmentInViewport";
import { buildPdpCartItem } from "@/lib/cart";
import { isFinishedGood } from "@/lib/product/product";
import { validatePdpForm } from "@/lib/product/validation";
import { cn } from "@/lib/utils";
import { useCartMutation } from "@/mutations/useCartMutation";
import { usePdpParams } from "@/providers/PdpParamsProvider";
import { useProduct } from "@/queries/useProduct";
import { useCustomQuantityPricing, useQuantityPricing } from "@/queries/useQuantityPricing";
import { usePdpStore } from "@/stores/usePdpStore";
import { useProductBuilderContext } from "./ProductBuilderContext";
import SaveForLaterButton from "./SaveForLaterButton";
function focusFirstError(errors: Record<string, string>) {
  const selector = Object.keys(errors).map(key => `[data-validation-field="${key}"]`).join(",");
  if (!selector) return;
  const focusContainer = () => {
    const container = document.querySelector(selector);
    if (!container) return;
    container.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
    const focusable = container.querySelector<HTMLElement>("button, input, select, textarea, [tabindex]");
    focusable?.focus({
      preventScroll: true
    });
  };
  const container = document.querySelector(selector);
  if (!container) return;

  // If the container is inside a hidden parent, we need to expand the hidden content before focusing on the element
  const hiddenParent = container.closest<HTMLElement>("[aria-hidden='true'][id]");
  if (!hiddenParent) {
    focusContainer();
    return;
  }
  const trigger = Array.from(document.querySelectorAll<HTMLButtonElement>("button[aria-controls]")).find(button => button.getAttribute("aria-controls") === hiddenParent.id);
  if (trigger?.getAttribute("aria-expanded") === "false") {
    trigger.click();
    requestAnimationFrame(focusContainer);
    return;
  }
  focusContainer();
}
function getButtonLabel(isEditMode: boolean, isPending: boolean) {
  if (isEditMode) return isPending ? "Updating..." : "Update Item";
  return isPending ? "Adding..." : "Add to Cart";
}
export default function ActionButtons() {
  const $ = _c(38);
  const {
    product
  } = useProductBuilderContext();
  const {
    itemId
  } = usePdpParams();
  const {
    data: productAdmin
  } = useProduct(product.sku);
  const {
    data: pricingData
  } = useQuantityPricing(productAdmin);
  const {
    data: customPricingData
  } = useCustomQuantityPricing(productAdmin);
  const setValidationErrors = usePdpStore(_temp);
  const isEditMode = !!itemId;
  let t0;
  if ($[0] !== productAdmin) {
    t0 = !productAdmin || !isFinishedGood(productAdmin);
    $[0] = productAdmin;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const showSaveForLater = t0;
  const mutation = useCartMutation(isEditMode);
  const addToCartRef = useRef(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      threshold: 1,
      rootMargin: "0px 0px -60% 0px"
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useTrackSegmentInViewport(addToCartRef, "Below the Fold Section in Viewport", t1);
  let t2;
  if ($[3] !== customPricingData || $[4] !== itemId || $[5] !== mutation || $[6] !== pricingData || $[7] !== productAdmin || $[8] !== setValidationErrors) {
    t2 = () => {
      if (!productAdmin) {
        return;
      }
      const errors = validatePdpForm(productAdmin);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        focusFirstError(errors);
        return;
      }
      mutation.mutate(buildPdpCartItem({
        productAdmin,
        pricingData,
        customPricingData,
        itemId
      }));
    };
    $[3] = customPricingData;
    $[4] = itemId;
    $[5] = mutation;
    $[6] = pricingData;
    $[7] = productAdmin;
    $[8] = setValidationErrors;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleSubmit = t2;
  const t3 = showSaveForLater ? "w-full" : "w-1/2";
  let t4;
  if ($[10] !== t3) {
    t4 = cn("flex items-start gap-4 min-[420px]:gap-8", t3);
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = mutation.isPending;
  let t6;
  if ($[12] !== isEditMode) {
    t6 = isEditMode ? <IconPencil /> : <IconShoppingCart />;
    $[12] = isEditMode;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== isEditMode || $[15] !== mutation.isPending) {
    t7 = getButtonLabel(isEditMode, mutation.isPending);
    $[14] = isEditMode;
    $[15] = mutation.isPending;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== handleSubmit || $[18] !== mutation.isPending || $[19] !== t6 || $[20] !== t7) {
    t8 = <Button ref={addToCartRef} size="xl" className="flex-1" onClick={handleSubmit} disabled={t5}>{t6}{t7}</Button>;
    $[17] = handleSubmit;
    $[18] = mutation.isPending;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== showSaveForLater) {
    t9 = showSaveForLater && <SaveForLaterButton />;
    $[22] = showSaveForLater;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t4 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className={t4}>{t8}{t9}</div>;
    $[24] = t4;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== isEditMode || $[29] !== mutation.isSuccess) {
    t11 = mutation.isSuccess && <output className="flex items-center gap-1 text-sm font-medium text-green-600"><IconCheck size={16} />{isEditMode ? "Item updated!" : "Item added to cart!"}</output>;
    $[28] = isEditMode;
    $[29] = mutation.isSuccess;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== isEditMode || $[32] !== mutation.isError) {
    t12 = mutation.isError && <p role="alert" className="text-sm font-medium text-red-600">{isEditMode ? "Failed to update item." : "Failed to add item to cart."}</p>;
    $[31] = isEditMode;
    $[32] = mutation.isError;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12) {
    t13 = <div className="flex flex-col gap-3">{t10}{t11}{t12}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}
function _temp(state) {
  return state.setValidationErrors;
}